/**
 * What an agent is doing right now, from its newest tool call (a `tool_use`
 * log line's meta). Shared by the API (live gate "running" light) and the web
 * (astronauts). Dependency-free.
 */
export interface HubActivityDto {
  /** The tool name from the run's newest tool_use line ("Bash", "Edit", …); null if none. */
  tool: string | null;
  /** That line's redacted input summary ("Bash $ npm test", "Edit src/a.ts"), ≤160 chars. */
  detail: string | null;
  /** When the tool call was logged (ISO). */
  at: string;
}

export type AgentActivity = 'read' | 'edit' | 'test' | 'ship' | 'run' | 'web' | 'think';

/** A tool call older than this no longer describes what the agent is doing. */
export const ACTIVITY_STALE_MS = 90_000;

const READ_TOOLS = new Set(['Read', 'Grep', 'Glob', 'LS']);
const EDIT_TOOLS = new Set(['Edit', 'MultiEdit', 'Write', 'NotebookEdit']);
const WEB_TOOLS = new Set(['WebFetch', 'WebSearch']);
const TEST_RE = /\b(npm (run )?test|vitest|jest|pytest|go test|cargo test|npm run (lint|typecheck|build))\b/;
const SHIP_RE = /\bgit (push|commit)\b|\bgh pr\b/;

export function isTestCommand(detail: string | null | undefined): boolean {
  return !!detail && TEST_RE.test(detail);
}

export function classifyActivity(a: HubActivityDto | null | undefined, nowMs: number): AgentActivity {
  if (!a || !a.tool) return 'think';
  const t = Date.parse(a.at);
  if (Number.isFinite(t) && nowMs - t > ACTIVITY_STALE_MS) return 'think';
  if (READ_TOOLS.has(a.tool)) return 'read';
  if (EDIT_TOOLS.has(a.tool)) return 'edit';
  if (WEB_TOOLS.has(a.tool)) return 'web';
  if (a.tool === 'Bash') {
    if (isTestCommand(a.detail)) return 'test';
    if (a.detail && SHIP_RE.test(a.detail)) return 'ship';
    return 'run';
  }
  return 'think';
}
